// Admin view for the public-onboarding pipeline. Lists submitted drafts,
// shows detail (full scraped + draft content), lets Elido approve/reject.
// Promotion to a real handyman client is left as a manual JSON export for v1.
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Sparkles, RefreshCw, Globe, Mail, CheckCircle2, X, AlertTriangle,
  Eye, Clipboard, ArrowLeft, ChevronRight,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

const STATUS_FILTERS = [
  { id: "all", label: "Alla" },
  { id: "trial_requested", label: "Trial begärd" },
  { id: "draft", label: "Utkast" },
  { id: "analyzing", label: "Analyserar" },
  { id: "approved", label: "Godkänd" },
  { id: "promoted", label: "Aktiverad" },
  { id: "rejected", label: "Avvisad" },
];

const STATUS_BADGE = {
  analyzing: { label: "Analyserar", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  draft: { label: "Utkast", cls: "bg-slate-50 text-slate-700 border-slate-200" },
  trial_requested: { label: "Trial begärd", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Godkänd", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  promoted: { label: "Aktiverad", cls: "bg-[#dcefe3] text-[#1c5b32] border-[#a7d4b6]" },
  rejected: { label: "Avvisad", cls: "bg-red-50 text-red-800 border-red-200" },
};

export default function OnboardingDraftsAdmin() {
  const { getAuthHeaders } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? `${API_URL}/api/admin/onboarding/drafts`
        : `${API_URL}/api/admin/onboarding/drafts?status=${filter}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setDrafts(data.drafts || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Kunde inte läsa utkast");
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return (
      <OnboardingDraftDetail
        draftId={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-onboarding-drafts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#255E69]" /> Onboarding-utkast
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Publik onboarding via /skapa-ai-agent. Granska och godkänn trial-förfrågningar.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="admin-onboarding-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="admin-onboarding-filters">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === f.id
                ? "bg-[#255E69] text-white border-[#255E69]"
                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
            }`}
            data-testid={`admin-onboarding-filter-${f.id}`}
          >
            {f.label}
            {f.id === "all"
              ? (counts._total != null && <span className="opacity-60 ml-1">({counts._total})</span>)
              : (counts[f.id] != null && <span className="opacity-60 ml-1">({counts[f.id]})</span>)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : drafts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">
            Inga {filter === "all" ? "" : STATUS_BADGE[filter]?.label.toLowerCase() + " "}utkast än
          </h3>
          <p className="text-sm text-gray-500">När någon skapar utkast via onboarding dyker de upp här.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {drafts.map((d) => (
            <DraftRow key={d.draft_id} draft={d} onOpen={() => setSelectedId(d.draft_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DraftRow({ draft, onOpen }) {
  const badge = STATUS_BADGE[draft.status] || STATUS_BADGE.draft;
  return (
    <div className="p-5 flex items-start justify-between gap-4" data-testid={`admin-onboarding-row-${draft.draft_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${badge.cls}`}
            data-testid={`admin-onboarding-status-${draft.status}`}
          >
            {badge.label}
          </span>
          {draft.detected_industry && (
            <span className="text-[11px] font-medium text-gray-700 bg-gray-100 rounded px-2 py-0.5">
              {draft.detected_industry}
            </span>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-900 mt-1">
          {draft.company_name || "(företag ej namngivet)"}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-0.5">
          {draft.website_url && (
            <span className="flex items-center gap-1 truncate max-w-[280px]">
              <Globe className="w-3 h-3" />
              <a
                href={draft.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#255E69] hover:underline truncate"
              >
                {draft.website_url}
              </a>
            </span>
          )}
          {draft.owner_email && (
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {draft.owner_email}</span>
          )}
          {draft.lead_email && draft.lead_email !== draft.owner_email && (
            <span className="text-gray-400">lead: {draft.lead_email}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[11px] text-gray-400 whitespace-nowrap">
          {draft.updated_at ? new Date(draft.updated_at).toLocaleString("sv-SE") : ""}
        </span>
        {draft.status === "promoted" && draft.promoted_to_client_id && (
          <Link
            to={`/admin/clients/${encodeURIComponent(draft.promoted_to_client_id)}`}
            className="text-xs font-medium text-[#255E69] hover:underline whitespace-nowrap inline-flex items-center gap-0.5"
            data-testid={`admin-onboarding-row-kund360-${draft.draft_id}`}
            onClick={(e) => e.stopPropagation()}
          >
            Kund 360 <ChevronRight className="w-3 h-3" />
          </Link>
        )}
        <Button size="sm" variant="outline" onClick={onOpen} data-testid={`admin-onboarding-open-${draft.draft_id}`}>
          <Eye className="w-3.5 h-3.5 mr-1" /> Öppna
        </Button>
      </div>
    </div>
  );
}

function OnboardingDraftDetail({ draftId, onBack }) {
  const { getAuthHeaders } = useAuth();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/drafts/${draftId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setDraft(data.draft);
      setNotes(data.draft?.admin_notes || "");
    } catch (e) {
      toast.error("Kunde inte läsa utkast");
    } finally {
      setLoading(false);
    }
  }, [draftId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    if (!window.confirm(`Sätt status till "${status}"?`)) return;
    setActing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/drafts/${draftId}/status`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ status, admin_notes: notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Kunde inte uppdatera"); return; }
      setDraft(data.draft);
      toast.success("Uppdaterat");
    } finally {
      setActing(false);
    }
  };

  const promoteDraft = async () => {
    if (!window.confirm(
      "Detta skapar en riktig klient i systemet och mejlar ägaren " +
      "installationskoden. Fortsätta?"
    )) return;
    setActing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/drafts/${draftId}/promote`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Kunde inte aktivera klient"); return; }
      if (data.already_promoted) {
        toast.info(`Klient finns redan: ${data.client_id}`);
      } else if (data.email_sent) {
        toast.success(`Klient ${data.client_id} skapad — snippet mejlat till ägaren`);
      } else {
        toast.success(`Klient ${data.client_id} skapad (mejl till ägaren misslyckades)`);
      }
      await load();
    } finally {
      setActing(false);
    }
  };

  const copyJson = () => {
    if (!draft) return;
    try {
      navigator.clipboard.writeText(JSON.stringify(draft, null, 2));
      toast.success("JSON kopierad — klistra in i ny klient");
    } catch (e) {
      toast.error("Kunde inte kopiera");
    }
  };

  if (loading || !draft) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  const badge = STATUS_BADGE[draft.status] || STATUS_BADGE.draft;

  return (
    <div className="space-y-5" data-testid="admin-onboarding-detail">
      <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="admin-onboarding-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Tillbaka
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {draft.company_name || "(företag ej namngivet)"}
          </h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${badge.cls}`}>
              {badge.label}
            </span>
            <a href={draft.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#255E69] hover:underline">
              {draft.website_url}
            </a>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-xs text-gray-500">{draft.owner_email}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={copyJson} data-testid="admin-onboarding-copy-json">
            <Clipboard className="w-3.5 h-3.5 mr-1" /> Kopiera JSON
          </Button>
          {draft.status !== "rejected" && (
            <Button size="sm" variant="outline" onClick={() => setStatus("rejected")} disabled={acting} data-testid="admin-onboarding-reject">
              <X className="w-3.5 h-3.5 mr-1" /> Avvisa
            </Button>
          )}
          {draft.status !== "approved" && draft.status !== "promoted" && (
            <Button size="sm" onClick={() => setStatus("approved")} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-onboarding-approve">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Godkänn
            </Button>
          )}
          {draft.status === "approved" && (
            <Button size="sm" onClick={() => promoteDraft()} disabled={acting} className="bg-[#255E69] hover:bg-[#1e4d56] text-white" data-testid="admin-onboarding-promote">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Skapa klient & skicka snippet
            </Button>
          )}
          {draft.status === "promoted" && draft.promoted_to_client_id && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="admin-onboarding-promoted-badge">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Klient: <code className="font-mono text-[11px]">{draft.promoted_to_client_id}</code>
              </span>
              {/* H3.2.1 — direkt-länk till Kund 360 från den nyss promoterade kunden */}
              <Link
                to={`/admin/clients/${encodeURIComponent(draft.promoted_to_client_id)}`}
                className="text-xs text-white bg-[#0F2A30] hover:bg-[#255E69] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="admin-onboarding-open-kund360"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Öppna Kund 360
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Kontakt & status">
          <Row k="Företag" v={draft.company_name} />
          <Row k="Bransch (AI)" v={draft.detected_industry} />
          <Row k="Lead-mejl" v={draft.lead_email} />
          <Row k="Kontakt (skapare)" v={draft.owner_email} />
          <Row k="Skapad" v={draft.created_at ? new Date(draft.created_at).toLocaleString("sv-SE") : ""} />
          <Row k="Trial begärd" v={draft.trial_requested_at ? new Date(draft.trial_requested_at).toLocaleString("sv-SE") : "—"} />
        </Card>
        <Card title="AI-förslag (text)">
          <Row k="Tonprofil" v={draft.suggested_tone} />
          <Row k="Primär färg" v={draft.suggested_primary_color} mono />
          {draft.suggested_logo_url && (
            <div className="py-2 flex items-start gap-3 text-xs border-b border-gray-50 last:border-b-0">
              <div className="text-gray-500 w-32 shrink-0 uppercase tracking-wider text-[10px] font-semibold pt-0.5">Logo</div>
              <div className="flex-1 flex items-center gap-3">
                <img src={draft.suggested_logo_url} alt="logo" className="w-10 h-10 rounded border border-gray-200 object-contain bg-white" data-testid="admin-onboarding-logo-preview" onError={(e) => { e.target.style.display = 'none'; }} />
                <a href={draft.suggested_logo_url} target="_blank" rel="noopener noreferrer" className="text-[#255E69] hover:underline break-all">{draft.suggested_logo_url}</a>
              </div>
            </div>
          )}
          <Row k="Startsvar" v={draft.suggested_start_message} />
          <Row k="Proaktiv bubbla" v={draft.suggested_proactive_message} />
        </Card>
      </div>

      <Card title="Tjänster">
        <Pills items={draft.suggested_services} />
      </Card>

      <Card title="Ärendeunderlag">
        <Pills items={draft.suggested_qualification_fields} mono />
      </Card>

      <Card title="Testfrågor">
        <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
          {(draft.suggested_test_questions || []).map((q, i) => <li key={i}>{q}</li>)}
        </ul>
      </Card>

      <Card title="FAQ-förslag">
        <div className="space-y-3">
          {(draft.suggested_faqs || []).map((f, i) => (
            <div key={i} className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-900">{f.question}</div>
              <div className="text-xs text-slate-600 mt-1">{f.answer}</div>
            </div>
          ))}
          {(draft.suggested_faqs || []).length === 0 && (
            <div className="text-xs text-gray-400">Inga FAQ-förslag.</div>
          )}
        </div>
      </Card>

      <Card title="Anteckningar (admin-interna)">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Tex. 'Hör av sig själv till installationsteamet'..."
          data-testid="admin-onboarding-notes"
        />
        <div className="text-[11px] text-gray-400 mt-1">Anteckningar sparas vid nästa status-ändring.</div>
      </Card>

      {draft.trial_request_notes && (
        <Card title="Kundens egna anteckningar">
          <div className="text-sm text-gray-800 whitespace-pre-wrap">{draft.trial_request_notes}</div>
        </Card>
      )}

      <ActivityLog editLog={draft.edit_log} chatLog={draft.preview_chat_log} />

      {draft.scrape_warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">{draft.scrape_warning}</div>
        </div>
      )}

      {draft.scraped_context && (
        <Card title="Scraped context (rådata)">
          <details>
            <summary className="cursor-pointer text-xs text-slate-500">Visa JSON</summary>
            <pre className="text-[11px] text-slate-700 bg-slate-50 rounded p-3 mt-2 overflow-x-auto">
              {JSON.stringify(draft.scraped_context, null, 2)}
            </pre>
          </details>
        </Card>
      )}
    </div>
  );
}

function ActivityLog({ editLog, chatLog }) {
  const edits = Array.isArray(editLog) ? editLog : [];
  const chats = Array.isArray(chatLog) ? chatLog : [];
  if (edits.length === 0 && chats.length === 0) {
    return (
      <Card title="Kundens aktivitet">
        <div className="text-xs text-gray-400">
          Inga ändringar eller chatt-frågor loggade ännu.
        </div>
      </Card>
    );
  }
  // Newest first
  const editsRev = [...edits].reverse();
  const chatsRev = [...chats].reverse();
  return (
    <Card title={`Kundens aktivitet (${edits.length} ändringar · ${chats.length} chatt-frågor)`}>
      {edits.length > 0 && (
        <details className="mb-3" open data-testid="admin-onboarding-edit-log">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700 mb-2">
            Ändringar i utkastet ({edits.length})
          </summary>
          <ul className="space-y-2 mt-2">
            {editsRev.map((e, i) => (
              <li
                key={i}
                className="text-xs border border-slate-100 bg-slate-50 rounded-lg p-2.5"
                data-testid="admin-onboarding-edit-log-entry"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-slate-800">{e.label || e.field}</span>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{formatTimestamp(e.at)}</span>
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                  <span className="text-slate-400 uppercase tracking-wider">Före</span>
                  <span className="text-slate-700 line-through opacity-70 break-words">{e.before || "—"}</span>
                  <span className="text-emerald-600 uppercase tracking-wider">Efter</span>
                  <span className="text-emerald-900 font-medium break-words">{e.after || "—"}</span>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
      {chats.length > 0 && (
        <details open data-testid="admin-onboarding-chat-log">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700 mb-2">
            Frågor kunden testat i förhandsgranskningen ({chats.length})
          </summary>
          <ul className="space-y-2 mt-2">
            {chatsRev.map((c, i) => (
              <li
                key={i}
                className="text-xs border border-slate-100 bg-slate-50 rounded-lg p-2.5"
                data-testid="admin-onboarding-chat-log-entry"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {c.type === "tone_tasting" && (
                    <span className="text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">Ton-test</span>
                  )}
                  {c.blocked === "off_topic" && (
                    <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Off-topic</span>
                  )}
                  {c.blocked === "incomplete_draft" && (
                    <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">Ofullständigt utkast</span>
                  )}
                  <span className="text-[10px] text-slate-400 ml-auto whitespace-nowrap">{formatTimestamp(c.at)}</span>
                </div>
                <div className="mb-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Kund</div>
                  <div className="text-slate-900 whitespace-pre-wrap break-words">{c.user_msg || "—"}</div>
                </div>
                {Array.isArray(c.variants) ? (
                  <div className="space-y-1.5">
                    {c.variants.map((v, j) => (
                      <div key={j} className="border-l-2 border-purple-300 pl-2">
                        <div className="text-[10px] uppercase tracking-wider text-purple-700 mb-0.5">
                          Bot — {v.label || v.key}
                        </div>
                        <div className="text-slate-800 whitespace-pre-wrap break-words">{v.reply || "—"}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Bot</div>
                    <div className="text-slate-800 whitespace-pre-wrap break-words">{c.bot_reply || "—"}</div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sv-SE", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-3">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-start gap-3 py-1.5 text-xs border-b border-gray-50 last:border-b-0">
      <div className="text-gray-500 w-32 shrink-0 uppercase tracking-wider text-[10px] font-semibold pt-0.5">{k}</div>
      <div className={`flex-1 text-gray-900 ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}

function Pills({ items, mono }) {
  if (!items || items.length === 0) return <div className="text-xs text-gray-400">Inga.</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span
          key={i}
          className={`text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-0.5 ${mono ? "font-mono" : ""}`}
        >
          {it}
        </span>
      ))}
    </div>
  );
}
